import { useState } from "react";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import { ShieldAlert, CheckCircle2, AlertTriangle, FileText, Mail, Phone } from "lucide-react";

const initialForm = {
  reporter_name: "", reporter_role: "", reporter_organization: "", reporter_email: "", reporter_phone: "", reporter_address: "",
  device_name: "", device_manufacturer: "", device_model: "", device_lot: "", device_udi: "", device_purchase_date: "",
  event_date: "", event_location: "", event_description: "",
  patient_involved: false, patient_age: "", patient_sex: "",
  outcome_death: false, outcome_serious_injury: false, outcome_malfunction: false, outcome_other: "",
  device_available_for_review: false, corrective_action_requested: "", additional_files_note: "",
};

export default function ComplaintFormPage() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const upd = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/complaints`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicSiteHeader />
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="complaint-success">
          <div className="bg-white border border-emerald-200 rounded-2xl p-8 shadow-sm">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mb-4" />
            <h1 className="text-3xl font-black text-slate-900 mb-3">Complaint Received</h1>
            <p className="text-slate-700 leading-relaxed mb-4">{result.message}</p>
            <p className="text-sm text-slate-600 mb-6">
              <strong>Reference ID:</strong>{" "}
              <code className="bg-slate-100 px-2 py-1 rounded text-xs">{result.reference_id}</code>
            </p>
            <div className="pt-4 border-t border-slate-200 text-sm text-slate-600 space-y-2">
              <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> compliance@sterilemate.com</p>
              <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> 800.988.3892</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none";
  const labelCls = "block text-xs font-semibold text-slate-700 mb-1.5";
  const sectionCls = "bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 shadow-sm";
  const h2 = "text-xl font-black text-slate-900 mb-1";
  const h2s = "text-xs text-slate-500 mb-5";

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicSiteHeader />

      {/* HERO */}
      <section className="bg-gradient-to-b from-red-50 via-white to-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-14 lg:py-16">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-red-100 border border-red-200 flex items-center justify-center shrink-0">
              <ShieldAlert className="w-7 h-7 text-red-700" />
            </div>
            <div>
              <p className="text-[11px] font-black uppercase tracking-[3px] text-red-700 mb-2">Medical Device Complaint Form</p>
              <h1 className="text-3xl lg:text-4xl font-black text-slate-900 leading-tight mb-3">Report a Medical Device Complaint</h1>
              <p className="text-slate-700 leading-relaxed">
                As a licensed medical-device supplier, <strong>Sterile Mate LLC</strong> maintains a formal complaint-intake process
                required under <strong>21 CFR Part 803</strong> (FDA MDR). Use this form to report any adverse event, malfunction,
                or quality concern involving a device we supplied. Reports involving death or serious injury are prioritized within
                one business day.
              </p>
            </div>
          </div>
          <div className="mt-6 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-900 leading-relaxed">
              <strong>Emergency? Do not use this form.</strong> Call 911 for medical emergencies. For device-related
              urgent recalls, call our compliance line at <a href="tel:8009883892" className="underline">800.988.3892</a>.
            </p>
          </div>
        </div>
      </section>

      <form onSubmit={submit} className="max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="complaint-form">
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-800 p-4 rounded-lg text-sm">
            <strong>Submission failed:</strong> {error}
          </div>
        )}

        {/* 1. REPORTER */}
        <section className={sectionCls}>
          <h2 className={h2}>1. Reporter Information</h2>
          <p className={h2s}>Who is filing this report</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className={labelCls}>Full name *</label><input required data-testid="cf-reporter-name" className={inputCls} value={form.reporter_name} onChange={upd("reporter_name")} /></div>
            <div><label className={labelCls}>Role / title</label><input placeholder="RN, biomed, EVS director, patient…" data-testid="cf-reporter-role" className={inputCls} value={form.reporter_role} onChange={upd("reporter_role")} /></div>
            <div><label className={labelCls}>Organization / facility</label><input data-testid="cf-reporter-org" className={inputCls} value={form.reporter_organization} onChange={upd("reporter_organization")} /></div>
            <div><label className={labelCls}>Email *</label><input required type="email" data-testid="cf-reporter-email" className={inputCls} value={form.reporter_email} onChange={upd("reporter_email")} /></div>
            <div><label className={labelCls}>Phone</label><input data-testid="cf-reporter-phone" className={inputCls} value={form.reporter_phone} onChange={upd("reporter_phone")} /></div>
            <div className="md:col-span-2"><label className={labelCls}>Mailing address</label><input data-testid="cf-reporter-address" className={inputCls} value={form.reporter_address} onChange={upd("reporter_address")} /></div>
          </div>
        </section>

        {/* 2. DEVICE */}
        <section className={sectionCls}>
          <h2 className={h2}>2. Device Information</h2>
          <p className={h2s}>Identify the device involved</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className={labelCls}>Device name / description *</label><input required data-testid="cf-device-name" className={inputCls} value={form.device_name} onChange={upd("device_name")} /></div>
            <div><label className={labelCls}>Manufacturer</label><input data-testid="cf-device-mfr" className={inputCls} value={form.device_manufacturer} onChange={upd("device_manufacturer")} /></div>
            <div><label className={labelCls}>Model number</label><input data-testid="cf-device-model" className={inputCls} value={form.device_model} onChange={upd("device_model")} /></div>
            <div><label className={labelCls}>Lot / serial number</label><input data-testid="cf-device-lot" className={inputCls} value={form.device_lot} onChange={upd("device_lot")} /></div>
            <div><label className={labelCls}>UDI (if known)</label><input data-testid="cf-device-udi" className={inputCls} value={form.device_udi} onChange={upd("device_udi")} /></div>
            <div><label className={labelCls}>Purchase / delivery date</label><input type="date" data-testid="cf-device-purchase-date" className={inputCls} value={form.device_purchase_date} onChange={upd("device_purchase_date")} /></div>
          </div>
        </section>

        {/* 3. EVENT */}
        <section className={sectionCls}>
          <h2 className={h2}>3. Event Details</h2>
          <p className={h2s}>What happened, when, and where</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className={labelCls}>Date of event *</label><input required type="date" data-testid="cf-event-date" className={inputCls} value={form.event_date} onChange={upd("event_date")} /></div>
            <div><label className={labelCls}>Location (facility / department)</label><input data-testid="cf-event-location" className={inputCls} value={form.event_location} onChange={upd("event_location")} /></div>
            <div className="md:col-span-2"><label className={labelCls}>Full description of complaint or event *</label><textarea required rows={6} data-testid="cf-event-desc" className={inputCls} value={form.event_description} onChange={upd("event_description")} placeholder="Describe what happened, how the device was being used, and any observations relevant to the complaint." /></div>
          </div>
        </section>

        {/* 4. PATIENT OUTCOME */}
        <section className={sectionCls}>
          <h2 className={h2}>4. Patient Outcome (MDR Classification)</h2>
          <p className={h2s}>Required for FDA reporting classification</p>
          <label className="flex items-center gap-2 mb-4 text-sm text-slate-800"><input type="checkbox" data-testid="cf-patient-involved" checked={form.patient_involved} onChange={upd("patient_involved")} className="w-4 h-4" /> A patient was involved in this event</label>
          {form.patient_involved && (
            <div className="grid md:grid-cols-2 gap-4 mb-5 pl-6 border-l-2 border-slate-200">
              <div><label className={labelCls}>Patient age</label><input data-testid="cf-patient-age" className={inputCls} value={form.patient_age} onChange={upd("patient_age")} /></div>
              <div><label className={labelCls}>Patient sex</label><select data-testid="cf-patient-sex" className={inputCls} value={form.patient_sex} onChange={upd("patient_sex")}><option value="">Prefer not to say</option><option>Female</option><option>Male</option><option>Other</option></select></div>
            </div>
          )}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-800"><input type="checkbox" data-testid="cf-outcome-death" checked={form.outcome_death} onChange={upd("outcome_death")} className="w-4 h-4" /> Event contributed to or caused a <strong>death</strong></label>
            <label className="flex items-center gap-2 text-sm text-slate-800"><input type="checkbox" data-testid="cf-outcome-serious" checked={form.outcome_serious_injury} onChange={upd("outcome_serious_injury")} className="w-4 h-4" /> Event caused a <strong>serious injury</strong></label>
            <label className="flex items-center gap-2 text-sm text-slate-800"><input type="checkbox" data-testid="cf-outcome-malfunction" checked={form.outcome_malfunction} onChange={upd("outcome_malfunction")} className="w-4 h-4" /> Device <strong>malfunctioned</strong> but no patient harm</label>
            <div><label className={labelCls}>Other outcome or context</label><input data-testid="cf-outcome-other" className={inputCls} value={form.outcome_other} onChange={upd("outcome_other")} /></div>
          </div>
        </section>

        {/* 5. FOLLOW-UP */}
        <section className={sectionCls}>
          <h2 className={h2}>5. Follow-up &amp; Corrective Action</h2>
          <label className="flex items-center gap-2 mb-4 text-sm text-slate-800"><input type="checkbox" data-testid="cf-device-available" checked={form.device_available_for_review} onChange={upd("device_available_for_review")} className="w-4 h-4" /> The device is available for return / inspection</label>
          <div className="mb-4"><label className={labelCls}>Corrective action requested</label><textarea rows={3} data-testid="cf-corrective-action" className={inputCls} value={form.corrective_action_requested} onChange={upd("corrective_action_requested")} placeholder="Replacement, credit, investigation report, formal recall notice, etc." /></div>
          <div><label className={labelCls}>Additional evidence you can provide</label><input data-testid="cf-additional-files" className={inputCls} value={form.additional_files_note} onChange={upd("additional_files_note")} placeholder="Photos, incident reports, biomed logs — we will follow up by email" /></div>
        </section>

        {/* SUBMIT */}
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <button type="submit" disabled={submitting} data-testid="cf-submit-btn" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-red-700 hover:bg-red-800 text-white font-black text-sm transition-all shadow-sm disabled:opacity-50">
            <FileText className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit Complaint"}
          </button>
          <p className="text-xs text-slate-500">
            Submissions are logged under 21 CFR Part 803. Sterile Mate reviews every report; if the event involves death or serious injury, a compliance officer will contact you within one business day.
          </p>
        </div>
      </form>
    </div>
  );
}
